import { useLayoutEffect, useRef, useState } from "react";

// USAGE:
// const { sentinelRef, stickyHeadRef, pinned, colWidths, headerRect, onScrollSync } =
//   useStickyGridHeader({ scrollRef, containerRef, getHeaderCells, deps });
//
// <div ref={sentinelRef} />
// <div ref={scrollRef} onScroll={() => { onScrollSync(); /* ...any other onScroll sync... */ }}>
//   <div ref={containerRef}>...real header + content...</div>
// </div>
// {pinned && (
//   <div style={{ position: "fixed", top: 0, left: headerRect.left, width: headerRect.width }}>
//     <div ref={stickyHeadRef} style={{ overflow: "hidden" }}>
//       ...clone of the header, one cell per colWidths entry...
//     </div>
//   </div>
// )}
//
// A sticky header for content nested inside a horizontally-scrolling
// container, done in JS instead of CSS `position: sticky`.
//
// WHY: an ancestor with `overflow-x: auto` gets its `overflow-y` computed
// as `auto` too — CSS forces the "visible" axis to `auto` once the other
// axis isn't visible, so that ancestor becomes a scroll container whether
// you meant it to or not. Since that container's height is normally
// unbounded (auto, always matching its own tall content), a
// `position: sticky` descendant never gets any scroll range to stick
// within — it renders exactly as if it were static, confirmed the hard way
// on Table before this hook existed. This sidesteps the trap entirely with
// an IntersectionObserver (which measures against the real browser
// viewport, unaffected by any ancestor's overflow/contain) driving a
// `position: fixed` clone that only exists once the real header has
// already scrolled off-screen — so there's never a moment where both the
// real header and the clone are visible at once.
//
// `sentinelRef` must be placed on a ~0-height element directly above
// `scrollRef`'s element — NOT on scrollRef itself. IntersectionObserver
// only fires on 0%<->0% crossings of the OBSERVED element, and scrollRef's
// element is normally tall (all the rows/cards below the header), so it'd
// stay >0% visible for nearly the entire scroll and the callback wouldn't
// fire until almost everything had scrolled past. A thin sentinel crosses
// out at exactly the moment the header reaches the viewport top.
export function useStickyGridHeader({ scrollRef, containerRef, getHeaderCells, deps = [] }) {
  const sentinelRef = useRef(null);
  const stickyHeadRef = useRef(null);
  const [pinned, setPinned] = useState(false);
  const [colWidths, setColWidths] = useState([]);
  const [headerRect, setHeaderRect] = useState({ left: 0, width: 0 });

  // Column widths, from the real header cells — observing just those cells
  // (not the whole, constantly-resizing content below, e.g. a row's detail
  // panel expanding) so unrelated height changes elsewhere don't trigger a
  // pointless re-measure + re-render of the clone.
  // `deps` is a caller-supplied re-measure trigger list (like useMirrorScrollbar's),
  // not a set of values read in this closure — Biome can't statically verify
  // a non-literal dependency array (same pre-existing gap in useMirrorScrollbar.js).
  useLayoutEffect(() => {
    const container = containerRef.current;
    const cells = container ? getHeaderCells(container) : [];
    if (!cells.length) return undefined;

    const measure = () => setColWidths(cells.map((cell) => cell.getBoundingClientRect().width));
    measure();

    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(measure);
    cells.forEach((cell) => {
      observer.observe(cell);
    });
    return () => observer.disconnect();
  }, deps);

  useLayoutEffect(() => {
    const el = sentinelRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return undefined;
    const observer = new IntersectionObserver(([entry]) => setPinned(entry.boundingClientRect.top < 0), {
      threshold: 0,
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Left/width of the fixed clone, matched to the real scroll container's
  // own box — re-measured on window resize and whenever the column set
  // changes (which can change the container's own width). Same non-literal
  // `deps` note as above.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    const updateRect = () => {
      const rect = el.getBoundingClientRect();
      setHeaderRect({ left: rect.left, width: rect.width });
    };
    updateRect();
    window.addEventListener("resize", updateRect);
    return () => window.removeEventListener("resize", updateRect);
  }, deps);

  // Keep the clone's horizontal scroll matched the instant it mounts —
  // `pinned` flips true mid-scroll, so the real scrollLeft is usually
  // already non-zero; without this it'd flash at scrollLeft 0 for a frame.
  // biome-ignore lint/correctness/useExhaustiveDependencies: scrollRef is a stable ref object, not a reactive value
  useLayoutEffect(() => {
    if (pinned && stickyHeadRef.current && scrollRef.current) {
      stickyHeadRef.current.scrollLeft = scrollRef.current.scrollLeft;
    }
  }, [pinned]);

  const onScrollSync = () => {
    if (stickyHeadRef.current && scrollRef.current) {
      stickyHeadRef.current.scrollLeft = scrollRef.current.scrollLeft;
    }
  };

  return { sentinelRef, stickyHeadRef, pinned, colWidths, headerRect, onScrollSync };
}

export default useStickyGridHeader;
